// src/components/timeBoxes/AllDayEventsSection.tsx
// 全天事件區塊 - 現代 chip 風格，支援展開/收合
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from '../../../components/common/icons';
import { Event } from '../../../types/calendar-event';
import { ScheduleItem } from '../../../types';
import { getItemDisplayTitle } from '../../../utils/calendarHelper';

// -- 截斷邏輯 --

const DEFAULT_MAX_VISIBLE = 3;

interface VisibleResult {
  visibleEvents: Event[];
  hiddenCount: number;
}

export const getVisibleAllDayEvents = (
  events: Event[],
  maxVisible: number,
  isExpanded: boolean
): VisibleResult => {
  if (isExpanded || events.length <= maxVisible) {
    return { visibleEvents: events, hiddenCount: 0 };
  }
  // 收合時：顯示前 (maxVisible - 1) 筆 + 第 maxVisible 行為 "+N more"
  const visibleEvents = events.slice(0, maxVisible - 1);
  const hiddenCount = events.length - (maxVisible - 1);
  return { visibleEvents, hiddenCount };
};

// -- 單一事件 Chip --

interface AllDayEventChipProps {
  event: Event;
  onSelect: (event: ScheduleItem) => void;
}

export const AllDayEventChip: React.FC<AllDayEventChipProps> = React.memo(({ event, onSelect }) => (
  <div
    className="bg-red-100 text-red-900 px-1.5 leading-5 cursor-pointer rounded text-xs font-medium truncate hover:brightness-95 transition-colors"
    onClick={() => onSelect(event as ScheduleItem)}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(event as ScheduleItem);
      }
    }}
    title={event.title || 'Untitled event'}
    aria-label={event.title || 'Untitled event'}
  >
    {getItemDisplayTitle(event as ScheduleItem)}
  </div>
));

AllDayEventChip.displayName = 'AllDayEventChip';

// -- "+N more" 指示器 --

interface MoreIndicatorProps {
  count: number;
  onClick: () => void;
}

const MoreIndicator: React.FC<MoreIndicatorProps> = React.memo(({ count, onClick }) => (
  <div
    className="text-xs text-gray-500 px-1.5 leading-5 cursor-pointer hover:text-gray-700 transition-colors"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    aria-label={`${count} more all-day events, click to expand`}
  >
    +{count} more
  </div>
));

MoreIndicator.displayName = 'MoreIndicator';

// -- 完整區塊組件 --

interface AllDayEventColumn {
  events: Event[];
}

interface AllDayEventsSectionProps {
  columns: AllDayEventColumn[];
  onSelectEvent: (event: ScheduleItem) => void;
  timeColumnWidth: number;
  maxVisible?: number;
}

const AllDayEventsSection: React.FC<AllDayEventsSectionProps> = React.memo(({
  columns,
  onSelectEvent,
  timeColumnWidth,
  maxVisible = DEFAULT_MAX_VISIBLE,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // 任一欄位超過上限才顯示 toggle
  const needsToggle = columns.some(col => col.events.length > maxVisible);

  return (
    <div
      className="bg-gray-50/80 border-b border-gray-200"
      role="region"
      aria-label="All-day events"
    >
      <div className="flex">
        {/* 左側時間欄：toggle 圖示 */}
        <div
          style={{ width: `${timeColumnWidth}px`, minWidth: `${timeColumnWidth}px` }}
          className="flex-shrink-0 border-r border-gray-200 flex items-center justify-center"
        >
          {needsToggle && (
            <button
              onClick={() => setIsExpanded(prev => !prev)}
              className="flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors w-full h-full min-h-[32px]"
              aria-label={isExpanded ? 'Collapse all-day events' : 'Expand all-day events'}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>

        {/* 右側日期欄 */}
        <div className="flex flex-1">
          {columns.map((col, colIndex) => {
            const { visibleEvents, hiddenCount } = getVisibleAllDayEvents(col.events, maxVisible, isExpanded);

            return (
              <div
                key={colIndex}
                className="flex-1 flex flex-col gap-0.5 py-0.5 px-0.5 border-r border-gray-200 last:border-r-0"
              >
                {visibleEvents.map((event) => (
                  <AllDayEventChip
                    key={event.id}
                    event={event}
                    onSelect={onSelectEvent}
                  />
                ))}
                {hiddenCount > 0 && (
                  <MoreIndicator
                    count={hiddenCount}
                    onClick={() => setIsExpanded(true)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

AllDayEventsSection.displayName = 'AllDayEventsSection';

export default AllDayEventsSection;
